import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import {
  ContactPreferencesResponse,
  OutstandingBalance,
  PatientSearchResponse,
  ScheduleAppointmentRequest,
  ScheduleAppointmentResponse,
} from '../models/schedule-appointment.models';

/**
 * Service for scheduling appointments and checking outstanding balances
 */
@Injectable()
export class ScheduleAppointmentService {
  constructor(
    private httpClient: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  /**
   * Fetches contact preferences for the current user or a linked account
   * GET /appointments/me/contact-preferences/lab-services
   * @param linkedPatientId - Optional patient master ID for linked accounts
   */
  getContactPreferences(
    linkedPatientId?: number | null
  ): Observable<ContactPreferencesResponse> {
    const url = `${this.environmentService.appointmentSchedulingBaseUrl}me/contact-preferences/lab-services`;

    // Add query parameter for linked accounts
    let params = new HttpParams();
    if (linkedPatientId) {
      params = params.set('linkedPid', linkedPatientId.toString());
    }

    return this.httpClient.get<ContactPreferencesResponse>(url, { params });
  }

  /**
   * Schedules an appointment with the provided details
   *
   * @param request - Schedule appointment request payload
   * @returns Observable of the schedule response
   */
  scheduleAppointment(
    request: ScheduleAppointmentRequest
  ): Observable<ScheduleAppointmentResponse> {
    const url = `${this.environmentService.appointmentSchedulingBaseUrl}schedule`;

    return this.httpClient.post<ScheduleAppointmentResponse>(url, request, {
      headers: { 'x-client-type': 'patient' },
    });
  }

  /**
   * Gets outstanding balances for a patient
   * Calls Patient Portal API /guest/invoicesForPerson
   *
   * @param firstName - Patient's first name
   * @param lastName - Patient's last name
   * @param dateOfBirth - Patient's date of birth (YYYY-MM-DD format)
   * @param zipCode - Patient's zip code
   * @returns Observable of outstanding balance information
   */
  getOutstandingBalances(
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    zipCode: string
  ): Observable<OutstandingBalance> {
    const url = `${this.environmentService.baseUrl}/guest/invoicesForPerson`;

    const params = new HttpParams()
      .set('firstName', firstName)
      .set('lastName', lastName)
      .set('dateOfBirth', dateOfBirth)
      .set('zipCode', zipCode);

    return this.httpClient.get<OutstandingBalance>(url, {
      params,
      headers: { 'x-client-type': 'patient' },
    });
  }

  /**
   * Search for patient by encrypted LPID to get complete patient data
   * Used for linked accounts that don't have complete address information
   *
   * @param encryptedLpid - Patient's encrypted LPID
   * @returns Observable of complete patient data including address
   */
  searchPatientByLpid(
    encryptedLpid: string
  ): Observable<PatientSearchResponse> {
    const url = `${this.environmentService.appointmentSchedulingBaseUrl}patients/search/unique`;

    return this.httpClient.post<PatientSearchResponse>(
      url,
      { lpid: encryptedLpid },
      {
        headers: { 'x-client-type': 'patient' },
      }
    );
  }
}
